import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, CheckCircle2, Clock3, LockOpen, Mic2, RefreshCw, Send, Sparkles, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/services/api";
import { DRILL_TIME_LIMIT_SECONDS } from "@/constants/timers";
import { useAudioRecording } from "@/hooks/useAudioRecording";

const TIMER_DEFAULT = DRILL_TIME_LIMIT_SECONDS;
const diagnosticQuestions = [
  { id: "block-1", question: "Quando alguém fala com você em inglês, o que mais acontece?", options: [{ label: "Tenho medo de errar", value: "fear_mistakes" }, { label: "Demoro para montar frase", value: "slow_processing" }, { label: "Entro em pânico com fala rápida", value: "listening_panic" }] },
  { id: "block-2", question: "Qual pensamento mais aparece antes de falar?", options: [{ label: "Minha pronúncia está ruim", value: "pronunciation_shame" }, { label: "Não sei palavras suficientes", value: "vocabulary_gap" }, { label: "Vou falar errado", value: "fear_mistakes" }] },
  { id: "block-3", question: "Em uma situação real, você mais precisa de ajuda para...", options: [{ label: "Pedir para repetir", value: "listening_panic" }, { label: "Ganhar tempo para pensar", value: "slow_processing" }, { label: "Usar uma frase coringa", value: "vocabulary_gap" }] },
  { id: "block-4", question: "O que te faria falar mais hoje?", options: [{ label: "Saber que erro pequeno é aceito", value: "fear_mistakes" }, { label: "Ter frase curta pronta", value: "vocabulary_gap" }, { label: "Treinar em voz alta sem vergonha", value: "pronunciation_shame" }] },
  { id: "block-5", question: "Na aula ao vivo, você quer praticar primeiro...", options: [{ label: "Responder mesmo com sotaque", value: "pronunciation_shame" }, { label: "Pedir fala mais devagar", value: "listening_panic" }, { label: "Organizar resposta rápida", value: "slow_processing" }] },
];

function MetricCard({ label, suffix = "%", value, testId }) {
  return (
    <article className="unlock-metric-card" data-testid={testId}>
      <span data-testid={`${testId}-label`}>{label}</span>
      <strong data-testid={`${testId}-value`}>{value}{suffix}</strong>
    </article>
  );
}

function DrillPicker({ activeDrillId, drills, onStart }) {
  return (
    <section className="unlock-drill-list" data-testid="unlock-drill-list">
      {drills.map((drill) => (
        <button key={drill.id} type="button" className={`unlock-drill-card ${activeDrillId === drill.id ? "unlock-drill-card-active" : ""}`} onClick={() => onStart(drill)} data-testid={`unlock-drill-card-${drill.id}`}>
          <span data-testid={`unlock-drill-level-${drill.id}`}>{drill.level}</span>
          <strong data-testid={`unlock-drill-title-${drill.id}`}>{drill.title}</strong>
          <small data-testid={`unlock-drill-situation-${drill.id}`}>{drill.situation_pt}</small>
        </button>
      ))}
    </section>
  );
}

function PressureTimer({ secondsLeft, total }) {
  const width = Math.max(0, Math.min(100, (secondsLeft / total) * 100));
  return (
    <div className="unlock-timer" data-testid="drill-pressure-timer">
      <div className="unlock-timer-topline">
        <span><Clock3 size={18} /> pressão controlada</span>
        <strong className={secondsLeft <= 5 ? "unlock-danger-time" : ""} data-testid="drill-pressure-seconds">{secondsLeft}s</strong>
      </div>
      <div className="unlock-timer-track" data-testid="drill-pressure-track"><span style={{ width: `${width}%` }} data-testid="drill-pressure-fill" /></div>
    </div>
  );
}

function FeedbackPanel({ result }) {
  if (!result) return null;
  return (
    <section className="unlock-feedback" data-testid="scoring-feedback-container">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid="unlock-feedback-kicker">resultado</p>
          <h2 className="font-display text-3xl font-black" data-testid="unlock-feedback-title">{result.passed ? "Você destravou esta frase." : "Revise e tente de novo."}</h2>
        </div>
        <span className="profile-badge" data-testid="unlock-total-score">{result.total_score}%</span>
      </div>
      <div className="mt-5 grid gap-4 md:grid-cols-3" data-testid="unlock-score-grid">
        <MetricCard label="Clareza" value={result.clarity} testId="unlock-clarity-score" />
        <MetricCard label="Velocidade" value={result.speed} testId="unlock-speed-score" />
        <MetricCard label="Confiança" value={result.confidence} testId="unlock-confidence-score" />
      </div>
      <p className="mt-5 text-base font-bold text-white/75" data-testid="unlock-feedback-copy">{result.feedback_pt}</p>
    </section>
  );
}

function ProgressStrip({ progress }) {
  return (
    <section className="unlock-progress-strip" data-testid="progress-history-chart">
      <MetricCard label="Sessões" suffix="" value={progress?.sessions_completed || 0} testId="unlock-progress-sessions" />
      <MetricCard label="Média" value={progress?.average_score || 0} testId="unlock-progress-average" />
      <MetricCard label="Melhor" value={progress?.best_score || 0} testId="unlock-progress-best" />
      <MetricCard label="Confiança" value={progress?.confidence_average || 0} testId="unlock-progress-confidence" />
    </section>
  );
}

function DiagnosticPanel({ answers, diagnostic, isPending, onAnswer, onSubmit }) {
  if (diagnostic) {
    return (
      <section className="unlock-diagnostic-panel" data-testid="unlock-diagnostic-result-panel">
        <div>
          <p className="section-kicker" data-testid="unlock-diagnostic-result-kicker">diagnóstico de bloqueio</p>
          <h2 className="font-display text-3xl font-black" data-testid="unlock-diagnostic-result-title">{diagnostic.block_label_pt}</h2>
          <p className="mt-3 text-sm font-bold text-white/75" data-testid="unlock-diagnostic-result-copy">{diagnostic.recommendation_pt}</p>
        </div>
      </section>
    );
  }
  const allAnswered = diagnosticQuestions.every((question) => answers[question.id]);
  return (
    <section className="unlock-diagnostic-panel" data-testid="unlock-diagnostic-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid="unlock-diagnostic-kicker">diagnóstico rápido</p>
          <h2 className="font-display text-3xl font-black" data-testid="unlock-diagnostic-title">Descubra por que sua fala trava.</h2>
        </div>
        <Brain size={30} />
      </div>
      <div className="mt-5 grid gap-4" data-testid="unlock-diagnostic-question-list">
        {diagnosticQuestions.map((question) => (
          <article key={question.id} className="unlock-diagnostic-question" data-testid={`unlock-diagnostic-question-${question.id}`}>
            <strong data-testid={`unlock-diagnostic-question-title-${question.id}`}>{question.question}</strong>
            <div className="mt-3 flex flex-wrap gap-2">
              {question.options.map((option) => <button key={option.value} type="button" className={`unlock-diagnostic-option ${answers[question.id] === option.value ? "unlock-diagnostic-option-active" : ""}`} onClick={() => onAnswer(question.id, option.value)} data-testid={`unlock-diagnostic-option-${question.id}-${option.value}`}>{option.label}</button>)}
            </div>
          </article>
        ))}
      </div>
      <button type="button" className="primary-action mt-5 w-auto" onClick={onSubmit} disabled={!allAnswered || isPending} data-testid="unlock-diagnostic-submit-button">
        <Sparkles size={18} /> {isPending ? "Calculando..." : "Personalizar treino"}
      </button>
    </section>
  );
}

function useUnlockTimer({ result, startedAt }) {
  const [secondsLeft, setSecondsLeft] = useState(TIMER_DEFAULT);
  const intervalRef = useRef(null);
  const stopTimer = useCallback(() => {
    if (!intervalRef.current) return;
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, [intervalRef]);

  useEffect(() => {
    stopTimer();
    if (!startedAt || result) return undefined;
    intervalRef.current = window.setInterval(() => setSecondsLeft((previousSeconds) => Math.max(0, previousSeconds - 1)), 1000);
    return stopTimer;
  }, [intervalRef, result, setSecondsLeft, startedAt, stopTimer]);
  const getElapsedSeconds = () => {
    if (!startedAt) return 0;
    return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
  };
  return { getElapsedSeconds, secondsLeft, setSecondsLeft };
}

function DrillQuestion({ attemptPending, audio, draft, error, onDraftChange, onReset, onStart, onSubmit, pressureSeconds, result, selectedDrill, secondsLeft, sessionPending }) {
  if (!selectedDrill) return <div data-testid="unlock-empty-state">Carregando drills...</div>;
  return (
    <>
      <PressureTimer secondsLeft={secondsLeft} total={pressureSeconds} />
      <p className="section-kicker mt-6" data-testid="unlock-active-situation">{selectedDrill.situation_pt}</p>
      <h2 className="unlock-guided-phrase" data-testid="drill-guided-phrase">{selectedDrill.target_phrase}</h2>
      <p className="mt-4 text-sm font-bold text-white/65" data-testid="unlock-confidence-tip"><Zap size={16} /> {selectedDrill.confidence_tip_pt}</p>
      <div className={`unlock-visualizer ${audio.isListening ? "unlock-visualizer-active" : ""}`} data-testid="drill-voice-visualizer"><span /><span /><span /><span /><span /></div>
      <textarea className="zero-textarea mt-5" value={draft} onChange={(event) => onDraftChange(event.target.value)} placeholder="Digite ou grave sua resposta em inglês" data-testid="unlock-response-input" />
      <div className="mt-5 flex flex-wrap gap-3" data-testid="unlock-controls">
        <button type="button" className="secondary-action w-auto" onClick={audio.handleMicrophoneClick} disabled={audio.isTranscribing} data-testid="unlock-microphone-button"><Mic2 size={18} /> {audio.isListening ? "Parar" : "Gravar"}</button>
        <button type="button" className="primary-action w-auto" onClick={onSubmit} disabled={!selectedDrill || !draft.trim() || attemptPending || sessionPending} data-testid="unlock-submit-button"><Send size={18} /> {sessionPending ? "Preparando..." : "Corrigir"}</button>
        <button type="button" className="secondary-action w-auto" onClick={onReset} data-testid="unlock-retry-button"><RefreshCw size={18} /> Reiniciar</button>
      </div>
      {audio.speechStatus && <p className="mt-3 text-sm font-black text-white/70" data-testid="unlock-speech-status">{audio.speechStatus}</p>}
      {error && <div className="auth-error mt-4" role="alert" data-testid="unlock-error-alert">{error}</div>}
      <FeedbackPanel result={result} />
      {result?.passed && result.next_drill && <button type="button" className="primary-action mt-5 w-auto" onClick={() => onStart(result.next_drill)} data-testid="unlock-next-drill-button"><CheckCircle2 size={18} /> Próxima frase</button>}
    </>
  );
}

export default function UnlockMode() {
  const queryClient = useQueryClient();
  const [activeDrill, setActiveDrill] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [draft, setDraft] = useState("");
  const [startedAt, setStartedAt] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [diagnosticAnswers, setDiagnosticAnswers] = useState({});
  const { data: drills = [] } = useQuery({ queryKey: ["unlock-drills"], queryFn: api.getUnlockDrills });
  const { data: progress } = useQuery({ queryKey: ["unlock-progress"], queryFn: api.getUnlockProgress });
  const { data: diagnostic } = useQuery({ queryKey: ["unlock-diagnostic"], queryFn: api.getUnlockDiagnostic });
  const selectedDrill = activeDrill || drills[0];
  const pressureSeconds = selectedDrill?.pressure_seconds || TIMER_DEFAULT;
  const audio = useAudioRecording({ disabled: false, onTranscript: setDraft });
  const { getElapsedSeconds, secondsLeft, setSecondsLeft } = useUnlockTimer({ result, startedAt });

  const sessionMutation = useMutation({
    mutationFn: (drillId) => api.createUnlockSession(drillId),
    onSuccess: (session) => {
      setActiveSession(session);
      setStartedAt(Date.now());
      setSecondsLeft(pressureSeconds);
      setResult(null);
      setDraft("");
      setError("");
    },
    onError: (err) => setError(formatApiErrorDetail(err.response?.data?.detail)),
  });
  const attemptMutation = useMutation({
    mutationFn: ({ sessionId }) => api.submitUnlockAttempt(sessionId, draft, getElapsedSeconds() || pressureSeconds),
    onSuccess: (attemptResult) => {
      setResult(attemptResult);
      setActiveSession(attemptResult.session);
      setStartedAt(null);
      queryClient.invalidateQueries({ queryKey: ["unlock-progress"] });
    },
    onError: (err) => setError(formatApiErrorDetail(err.response?.data?.detail)),
  });
  const diagnosticMutation = useMutation({
    mutationFn: () => api.submitUnlockDiagnostic(diagnosticQuestions.map((question) => ({ question_id: question.id, answer: diagnosticAnswers[question.id] }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unlock-diagnostic"] });
      queryClient.invalidateQueries({ queryKey: ["unlock-drills"] });
      setDiagnosticAnswers({});
    },
    onError: (err) => setError(formatApiErrorDetail(err.response?.data?.detail)),
  });

  const startDrill = (drill) => {
    setActiveDrill(drill);
    sessionMutation.mutate(drill.id);
  };
  const resetCurrent = () => {
    if (selectedDrill) startDrill(selectedDrill);
  };
  const submit = async () => {
    if (!draft.trim() || !selectedDrill || attemptMutation.isPending || sessionMutation.isPending) return;
    const session = activeSession || await sessionMutation.mutateAsync(selectedDrill.id);
    attemptMutation.mutate({ sessionId: session.id });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="unlock-page">
      <section className="unlock-hero" data-testid="unlock-hero-section">
        <div>
          <p className="section-kicker" data-testid="unlock-kicker">modo destravar</p>
          <h1 className="mt-4 font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="unlock-heading">Fale antes da mente pedir perfeição.</h1>
          <p className="mt-4 max-w-3xl text-base font-bold text-white/75 md:text-lg" data-testid="unlock-description">Treinos curtos com pressão controlada, frase segura e pontuação de clareza, velocidade e confiança.</p>
        </div>
        <button type="button" className="primary-action w-auto" onClick={() => selectedDrill && startDrill(selectedDrill)} disabled={!selectedDrill || sessionMutation.isPending} data-testid="unlock-start-button">
          <LockOpen size={18} /> Iniciar Drill
        </button>
      </section>

      <ProgressStrip progress={progress} />
      <DiagnosticPanel answers={diagnosticAnswers} diagnostic={diagnostic} isPending={diagnosticMutation.isPending} onAnswer={(questionId, answer) => setDiagnosticAnswers((current) => ({ ...current, [questionId]: answer }))} onSubmit={() => diagnosticMutation.mutate()} />

      <section className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]" data-testid="unlock-layout">
        <DrillPicker activeDrillId={selectedDrill?.id} drills={drills} onStart={startDrill} />
        <article className="unlock-focus-panel" data-testid="unlock-focus-panel">
          <DrillQuestion attemptPending={attemptMutation.isPending} audio={audio} draft={draft} error={error} onDraftChange={setDraft} onReset={resetCurrent} onStart={startDrill} onSubmit={submit} pressureSeconds={pressureSeconds} result={result} selectedDrill={selectedDrill} secondsLeft={secondsLeft} sessionPending={sessionMutation.isPending} />
        </article>
      </section>
    </div>
  );
}